import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, FilePlus, TrendingUp, Clock, CheckCircle, Search, ArrowRight, BarChart3, FolderOpen, Percent, Timer, Briefcase, Filter, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Filters
  const [filters, setFilters] = useState({ period: 'this_month', responsible: '', tipo: '', date_from: '', date_to: '' });
  const [filterOptions, setFilterOptions] = useState({ tipos: [], users: [] });
  const [showFilters, setShowFilters] = useState(false);

  const isAdmin = user?.role === 'Administrador' || user?.role === 'Leitor/Gestor';

  const visibleRecentDocs = useMemo(() => {
    const docs = stats?.recent_documents || [];
    const s = search.toLowerCase();
    return docs
      .filter(doc => !search
        || doc.title?.toLowerCase().includes(s)
        || doc.tipo?.toLowerCase().includes(s)
        || doc.client_name?.toLowerCase().includes(s))
      .slice(0, 8);
  }, [stats?.recent_documents, search]);

  const loadFilterOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/dashboard/filter-options`, { withCredentials: true });
      setFilterOptions(res.data);
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const params = { period: filters.period };
      if (filters.responsible) params.responsible = filters.responsible;
      if (filters.tipo) params.tipo = filters.tipo;
      if (filters.period === 'custom') {
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
      }

      const [statsRes, tasksRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`, { withCredentials: true, params }),
        axios.get(`${API_URL}/my-tasks`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setTasks(tasksRes.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadFilterOptions(); }, [loadFilterOptions]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const clearFilters = () => {
    setFilters({ period: 'this_month', responsible: '', tipo: '', date_from: '', date_to: '' });
  };

  const hasActiveFilters = filters.period !== 'this_month' || filters.responsible || filters.tipo;

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const getStatusColor = (status) => {
    const map = {
      'Rascunho': 'bg-gray-100 text-gray-600',
      'Em Revisão': 'bg-amber-50 text-amber-600',
      'Aprovado': 'bg-blue-50 text-blue-600',
      'Finalizado': 'bg-emerald-50 text-emerald-600',
      'Arquivado': 'bg-red-50 text-red-600',
    };
    return map[status] || 'bg-gray-100 text-gray-600';
  };

  const getTaskBorder = (type) => {
    const map = {
      assigned_to_me: 'border-l-emerald-400',
      review_pending: 'border-l-amber-400',
      finalize_pending: 'border-l-blue-400',
      draft_pending: 'border-l-gray-300',
      returned: 'border-l-red-400',
    };
    return map[type] || 'border-l-gray-200';
  };

  const formatReviewTime = (hours) => {
    if (!hours || hours === 0) return '--';
    if (hours < 1) return `${Math.round(hours * 60)}min`;
    if (hours < 24) return `${hours}h`;
    return `${Math.round(hours / 24)}d`;
  };

  const periodLabels = {
    this_month: 'Este mês',
    last_month: 'Mês passado',
    this_quarter: 'Este trimestre',
    this_year: 'Este ano',
    custom: 'Personalizado'
  };

  const TypeChart = () => {
    const data = stats?.docs_by_type || {};
    const entries = Object.entries(data);
    if (entries.length === 0) return <p className="text-xs text-[#AEAEB2] text-center py-2">Sem dados no período</p>;
    const total = entries.reduce((s, [, v]) => s + v, 0);
    const typeColors = { 'Proposta Comercial': 'bg-[#AF2628]', 'ETP': 'bg-blue-500', 'TR': 'bg-amber-500', 'Documento Auxiliar': 'bg-gray-400' };
    return (
      <div className="space-y-2">
        {entries.map(([tipo, count]) => (
          <div key={tipo} className="flex items-center gap-2">
            <div className="flex-1">
              <div className="flex justify-between text-xs mb-0.5">
                <span className="text-[#6E6E73] truncate">{tipo}</span>
                <span className="font-medium text-[#111111]">{count}</span>
              </div>
              <div className="bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div className={`h-full rounded-full ${typeColors[tipo] || 'bg-gray-400'} transition-all duration-500`} style={{ width: `${(count / total) * 100}%` }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <p className="text-sm text-[#6E6E73] mb-0.5">{getGreeting()},</p>
          <h1 className="text-3xl font-bold text-[#111111]">{user?.name?.split(' ')[0] || 'Dashboard'}</h1>
        </div>
        <p className="text-sm text-[#AEAEB2]">
          {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-[#E5E5EA] rounded-xl p-3">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${hasActiveFilters ? 'bg-[#AF2628] text-white' : 'bg-gray-100 text-[#6E6E73] hover:bg-gray-200'}`}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-3.5 h-3.5" />
            Filtros
            {hasActiveFilters && <span className="bg-white/20 text-[10px] px-1.5 py-0.5 rounded-full">{[filters.period !== 'this_month' && 1, filters.responsible && 1, filters.tipo && 1].filter(Boolean).length}</span>}
          </button>

          {/* Quick period pills */}
          <div className="flex items-center gap-1">
            {['this_month', 'last_month', 'this_quarter', 'this_year'].map(p => (
              <button
                key={p}
                onClick={() => setFilters(prev => ({ ...prev, period: p }))}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filters.period === p ? 'bg-[#111111] text-white' : 'bg-gray-50 text-[#6E6E73] hover:bg-gray-100'}`}
                data-testid={`filter-period-${p}`}
              >
                {periodLabels[p]}
              </button>
            ))}
          </div>

          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs text-[#AF2628] hover:text-[#961B22] flex items-center gap-1 ml-auto" data-testid="clear-filters-btn">
              <X className="w-3 h-3" /> Limpar filtros
            </button>
          )}
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="flex items-end gap-3 mt-3 pt-3 border-t border-[#E5E5EA] flex-wrap">
            <div className="min-w-[160px]">
              <label className="text-[11px] text-[#6E6E73] font-medium block mb-1">Tipo de Documento</label>
              <Select value={filters.tipo || 'all'} onValueChange={v => setFilters(prev => ({ ...prev, tipo: v === 'all' ? '' : v }))}>
                <SelectTrigger className="h-9 text-sm" data-testid="filter-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {filterOptions.tipos.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {isAdmin && filterOptions.users.length > 0 && (
              <div className="min-w-[200px]">
                <label className="text-[11px] text-[#6E6E73] font-medium block mb-1">Responsável</label>
                <Select value={filters.responsible || 'all'} onValueChange={v => setFilters(prev => ({ ...prev, responsible: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="h-9 text-sm" data-testid="filter-responsible"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {filterOptions.users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="min-w-[160px]">
              <label className="text-[11px] text-[#6E6E73] font-medium block mb-1">Período</label>
              <Select value={filters.period} onValueChange={v => setFilters(prev => ({ ...prev, period: v }))}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="this_month">Este mês</SelectItem>
                  <SelectItem value="last_month">Mês passado</SelectItem>
                  <SelectItem value="this_quarter">Este trimestre</SelectItem>
                  <SelectItem value="this_year">Este ano</SelectItem>
                  <SelectItem value="custom">Personalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {filters.period === 'custom' && (
              <>
                <div>
                  <label className="text-[11px] text-[#6E6E73] font-medium block mb-1">De</label>
                  <Input type="date" value={filters.date_from} onChange={e => setFilters(prev => ({ ...prev, date_from: e.target.value }))} className="h-9 text-sm w-[150px]" data-testid="filter-date-from" />
                </div>
                <div>
                  <label className="text-[11px] text-[#6E6E73] font-medium block mb-1">Até</label>
                  <Input type="date" value={filters.date_to} onChange={e => setFilters(prev => ({ ...prev, date_to: e.target.value }))} className="h-9 text-sm w-[150px]" data-testid="filter-date-to" />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {[
          { value: stats?.total_in_period || 0, label: filters.tipo ? `${filters.tipo}` : 'Docs no período', icon: TrendingUp, iconBg: 'bg-[#FFF1F0]', iconColor: 'text-[#AF2628]' },
          { value: stats?.drafts_count || 0, label: 'Em rascunho', icon: Clock, iconBg: 'bg-amber-50', iconColor: 'text-amber-500' },
          { value: stats?.finalized_count || 0, label: 'Finalizados', icon: CheckCircle, iconBg: 'bg-emerald-50', iconColor: 'text-emerald-500' },
          { value: tasks.length, label: 'Tarefas pendentes', icon: FolderOpen, iconBg: 'bg-blue-50', iconColor: 'text-blue-500' },
          { value: `${stats?.approval_rate || 0}%`, label: 'Taxa de aprovação', icon: Percent, iconBg: 'bg-purple-50', iconColor: 'text-purple-500' },
          { value: formatReviewTime(stats?.avg_review_hours), label: 'Tempo méd. revisão', icon: Timer, iconBg: 'bg-teal-50', iconColor: 'text-teal-500' },
        ].map((kpi, idx) => (
          <div key={kpi.label} className="bg-white border border-[#E5E5EA] rounded-xl p-4 hover:shadow-sm transition-shadow" data-testid={`kpi-card-${idx}`}>
            <div className={`${kpi.iconBg} w-8 h-8 rounded-lg flex items-center justify-center mb-2`}>
              <kpi.icon className={`w-4 h-4 ${kpi.iconColor}`} />
            </div>
            <p className="text-xl font-bold text-[#111111]">{kpi.value}</p>
            <p className="text-[11px] text-[#6E6E73] mt-0.5">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-semibold text-[#6E6E73] mb-3">Ações Rápidas</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { title: 'Proposta Comercial', icon: FileText, color: 'bg-[#FFF1F0]', iconColor: 'text-[#AF2628]', tipo: 'Proposta Comercial' },
            { title: 'Estudo Técnico (ETP)', icon: FilePlus, color: 'bg-[#F0F9FF]', iconColor: 'text-[#0EA5E9]', tipo: 'ETP' },
            { title: 'Termo de Referência', icon: FileText, color: 'bg-[#FFFBEB]', iconColor: 'text-[#D97706]', tipo: 'TR' },
            { title: 'Novo Documento', icon: Briefcase, color: 'bg-[#F0FDF4]', iconColor: 'text-[#22C55E]', tipo: null },
          ].map((action, idx) => (
            <button
              key={action.title}
              onClick={() => action.tipo ? navigate('/documents/new', { state: { tipo: action.tipo } }) : navigate('/documents/new')}
              data-testid={`quick-action-${idx}`}
              className="bg-white border border-[#E5E5EA] rounded-xl p-4 hover:shadow-md hover:border-[#AF2628]/30 transition-all text-left group"
            >
              <div className={`${action.color} w-9 h-9 rounded-lg flex items-center justify-center mb-2 group-hover:scale-105 transition-transform`}>
                <action.icon className={`w-4 h-4 ${action.iconColor}`} />
              </div>
              <h3 className="font-medium text-sm text-[#111111] group-hover:text-[#AF2628] transition-colors">{action.title}</h3>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* Left: Tarefas */}
        <div className="xl:col-span-5 space-y-5">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-[#111111]">Minhas Tarefas</h2>
                {tasks.length > 0 && (
                  <span className="bg-[#AF2628] text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid="tasks-badge">{tasks.length}</span>
                )}
              </div>
              <button onClick={() => navigate('/my-tasks')} className="text-xs text-[#AF2628] hover:text-[#961B22] font-medium flex items-center gap-1">
                Todas <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <div className="space-y-2">
              {tasks.length > 0 ? tasks.slice(0, 6).map((task) => (
                <div
                  key={`${task.document_id}-${task.task_type}`}
                  onClick={() => navigate(`/documents/${task.document_id}`)}
                  className={`bg-white border border-[#E5E5EA] border-l-4 ${getTaskBorder(task.task_type)} rounded-lg p-3 hover:shadow-sm cursor-pointer transition-all`}
                  data-testid={`dashboard-task-${task.document_id}`}
                >
                  <h4 className="font-medium text-sm text-[#111111] truncate">{task.title}</h4>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[11px] text-[#AEAEB2] truncate">{task.client_name}</span>
                    <span className="text-[11px] font-medium text-[#AF2628] shrink-0 ml-2">{task.task_label}</span>
                  </div>
                </div>
              )) : (
                <div className="bg-white border border-[#E5E5EA] rounded-lg p-8 text-center">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-300" />
                  <p className="text-sm text-[#6E6E73]">Tudo em dia!</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#E5E5EA] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4 text-[#6E6E73]" />
              <h3 className="text-sm font-semibold text-[#111111]">Documentos por Tipo</h3>
              <span className="text-[10px] text-[#AEAEB2] ml-auto">{periodLabels[filters.period]}</span>
            </div>
            <TypeChart />
          </div>
        </div>

        {/* Right: Documentos Recentes */}
        <div className="xl:col-span-7">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-[#111111]">Documentos Recentes</h2>
            <button onClick={() => navigate('/documents')} className="text-sm text-[#AF2628] hover:text-[#961B22] font-medium flex items-center gap-1">
              Ver todos <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="bg-white border border-[#E5E5EA] rounded-xl overflow-hidden">
            <div className="p-3 border-b border-[#E5E5EA]">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-[#6E6E73]" />
                <Input placeholder="Filtrar documentos..." value={search} onChange={(e) => setSearch(e.target.value)} className="border-0 shadow-none focus-visible:ring-0 h-8 text-sm" data-testid="dashboard-search-input" />
              </div>
            </div>
            {stats?.recent_documents?.length > 0 ? (
              <div className="divide-y divide-[#E5E5EA]">
                {visibleRecentDocs
                  .map((doc) => (
                    <div key={doc.document_id} onClick={() => navigate(`/documents/${doc.document_id}`)} className="p-4 hover:bg-[#FAFAFA] cursor-pointer transition-colors" data-testid={`recent-doc-${doc.document_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm text-[#111111] truncate">{doc.title}</h3>
                          <div className="flex items-center gap-2 mt-1 text-xs text-[#AEAEB2]">
                            <span className="bg-gray-100 text-[#6E6E73] px-1.5 py-0.5 rounded text-[10px]">{doc.tipo}</span>
                            <span className="truncate">{doc.client_name}</span>
                            <span>·</span>
                            <span>{new Date(doc.updated_at).toLocaleDateString('pt-BR')}</span>
                          </div>
                        </div>
                        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium ${getStatusColor(doc.status)} ml-3 shrink-0`}>{doc.status}</span>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="p-10 text-center">
                <FileText className="w-10 h-10 mx-auto mb-3 text-[#E5E5EA]" />
                <p className="text-sm text-[#6E6E73]">Nenhum documento criado ainda</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
